import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import "./ContractorProfile.css";
import { generatePrincipalEmployerCode } from '../../components/GenerateAttendanceCode';

const PrincipalEmployerProfile = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData] = useFetch();
  const [userId, setUserId] = useState(authState.user._id);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [userName, setUserName] = useState(authState.user.name);
  const [principalEmployerCode, setPrincipalEmployerCode] = useState(generatePrincipalEmployerCode());

  const [profileData, setProfileData] = useState({
    userCode: userCode,
    userId: userId,
    userName: userName,
    employerName: '',
    principalEmployerCode: principalEmployerCode,
    employerLocation: '',
    employerAddress: '',
    ownerName: '',
    ownerAddress: '',
    relativeName: '',
    relatiosnhip: '',
    managerCode: '',
    managerName: '',
    managerDesignation: '',
    managerAddress: '',
    remarks: '',
    isActive: false,
    isDeleted: false,
    createdBy: userId,
    dateCreated: new Date(),
  });

  const [originalProfileData, setOriginalProfileData] = useState({});
  const [profileExists, setProfileExists] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState('');
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserProfile(userId);
  }, [userId]);

  const fetchUserProfile = useCallback(() => {
    try {
      const config = {
        url: "/principalemployer/byid",
        method: 'post',
        data: { userId: userId },
        headers: { Authorization: authState.token },
      };
      fetchData(config)
        .then(response => {
          // console.log("profile response ",response);

          if (response && response.status) {
            // console.log("profile exists ",response);
            setProfileExists(true);
            const { profile } = response;
            setProfileId(profile._id);
            setProfileData({
              ...profileData,
              ...profile,
            });
          } else {
            setCreateSuccessMsg("Sorry! Principal Employer Not Found. Please retry.");
          }
        })
        .catch(() => {
          setCreateSuccessMsg("Sorry! Principal Employer Not Found. Please retry.");
        });
    } catch {
      setCreateSuccessMsg("Sorry! Principal Employer not found. Please retry.");
    }
  }, [userId, fetchData, setProfileExists]);

  const handleProfileUpdate = useCallback(() => {
    validateInputs();
    const config = {
      url: `/principalemployer/updateprofile`,
      method: 'post',
      data: { profileData: profileData, userId: userId},
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setOriginalProfileData(profileData);
          setUpdateSuccess(true);
          setUpdateSuccessMsg("Principal Employer updated successfully!");
        } else {
          setUpdateSuccessMsg("Error updating Principal Employer! Please retry.");
        }
      })
      .catch(() => {
        setUpdateSuccessMsg("Error updating Principal Employer! Please retry.");
      });
  }, [profileData, profileId, fetchData]);

  const handleCancel = () => {
    setProfileData(originalProfileData);
  };

  const handleCreateProfile = () => {
    setCreatingProfile(true);
    fetchProfileCreate();
  };

  const fetchProfileCreate = useCallback(() => {
    const config = {
      url: "/principalemployer",
      method: 'post',
      data: profileData,
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setCreateSuccess(true);
          setCreateSuccessMsg("Principal Employer Created Successfully");
        } else {
          setCreateSuccessMsg("Sorry! Principal Employer Not Created. Please retry.");
        }
      })
      .catch(() => {
        setCreateSuccessMsg("Sorry! Principal Employer Not Created. Please retry.");
      });
  }, [profileData, fetchData]);

  const validateInputs = () => {
    let isValid = true;
    const errors = {};
    const mobileRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!profileData.employerName) {
      errors.employerName = 'Name is required.';
      isValid = false;
    }

    if (!profileData.natureOfWork) {
      errors.natureOfWork = 'Nature of Work is required.';
      isValid = false;
    }

    if (!profileData.address) {
      errors.address = 'Address is required.';
      isValid = false;
    }
    setErrors(errors);
    return isValid;
  };

  return (
    <MainLayout>
      <div className='text-lg mt-12 mb-4 mx-8 border-b border-b-gray-300 text-white'>
        <h2>Principal Employer Master</h2>
      </div>
      <div className='main-content mt-4 border border-b-gray-300 p-6 rounded-lg bg-gray-800 text-white'>
        {!profileExists && (
          <div className='flex justify-center mt-4'>
            <button
              className='create-profile-button px-4 py-2 bg-blue-600 rounded-full text-white hover:bg-blue-700'
              onClick={handleCreateProfile}
              disabled={creatingProfile}
            >
              Initiate Your Principal Employer Master Record Creation
            </button>
            {createSuccess && (
              <p className='text-lg text-green-500'>Your Principal Employer Profile Setup Successfully!! Come back on this page again to setup your details.</p>
            )}
          </div>
        )}
        {profileExists && (
          <>
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col'>
                <label >Principal Employer Think5Whys Code:</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="text"
                  value={profileData.principalEmployerCode}
                  disabled
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Principal Employer Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.employerName}
                    onChange={(e) => setProfileData({ ...profileData, employerName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Principal Employer Location:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.employerLocation}
                    onChange={(e) => setProfileData({ ...profileData, employerLocation: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Principal Employer Address:</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '150px',
                    }}
                    value={profileData.employerAddress}
                    onChange={(e) => setProfileData({ ...profileData, employerAddress: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Email:</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="email"
                  value={profileData.entityEmail}
                  onChange={(e) => setProfileData({ ...profileData, entityEmail: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Nature of Work:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.natureOfWork}
                    onChange={(e) => setProfileData({ ...profileData, natureOfWork: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Owner Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.ownerName}
                    onChange={(e) => setProfileData({ ...profileData, ownerName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Owner Address:</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '150px',
                    }}
                    value={profileData.ownerAddress}
                    onChange={(e) => setProfileData({ ...profileData, ownerAddress: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Owner's Father / Husband Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.relativeName}
                    onChange={(e) => setProfileData({ ...profileData, relativeName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Manager Think5Whys User Code:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.managerCode}
                    onChange={(e) => setProfileData({ ...profileData, managerCode: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Manager Name:</label>
                <input
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    type="text"
                    value={profileData.managerName}
                    onChange={(e) => setProfileData({ ...profileData, managerName: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Manager Designation:</label>
                <input 
                    className="profile-input bg-gray-600 rounded-md px-2 py-1"
                    style={{ border: '1px solid #ccc'}}
                    type="text"
                    value={profileData.managerDesignation}
                    onChange={(e) => setProfileData({ ...profileData, managerDesignation: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Manager Address:</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '150px',
                    }}
                    value={profileData.managerAddress}
                    onChange={(e) => setProfileData({ ...profileData, managerAddress: e.target.value })}
                />
              </div>

              <div className='flex flex-col'>
                <label>Remarks:</label>
                <textarea
                    className="bg-gray-600 rounded-md px-2 py-1"
                    style={{
                        border: '1px solid #ccc',
                        maxWidth: '650px',
                        maxHeight: '100px',
                    }}
                    value={profileData.remarks}
                    onChange={(e) => setProfileData({ ...profileData, remarks: e.target.value })}
                />
              </div>
            </div>
            {updateSuccess && (
              <div className="success-message text-green-500 text-lg mt-4">
                {updateSuccessMsg}
              </div>
            )}
            <div className='flex mt-6'>
              <button
                className='update-profile-button px-4 py-2 bg-green-600 rounded-full text-white hover:bg-green-700 w-40'
                onClick={handleProfileUpdate}
              >
                Update
              </button>
              <button
                className='update-profile-button px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 w-40'
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </>
        )}

        {!profileExists && createSuccess && (
          <div className="flex flex-col justify-center success-message text-green-500 text-xl mt-4">
            {createSuccessMsg}
            <button
                className='update-profile-button px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 w-40'
                onClick={() => navigate("/think-contract-principalEmployer")}
              >
                OK
            </button>
          </div>
        )}
     </div>
    </MainLayout>
  );
};

export default PrincipalEmployerProfile;



